import type { ScenarioEntry, ScenarioProjection } from '../domain/types';
import {
  entryDescription,
  entryStudyRelation,
  issueLabels,
  issueRelations,
  mappingNature,
  publicReferences,
  publicSourceChecked,
  type DeviceResearch,
} from './scenario-research-reference';
import { ScenarioCriticalUnknowns } from './ScenarioCriticalUnknowns';
import { SourceFields } from './ScenarioEntryInspector';
import { ScenarioSourceInspector } from './ScenarioSourceInspector';
import { useModalFocus } from './scenario-modal-focus';

/**
 * The source-and-version drawer — a MAINTENANCE surface, not business content.
 *
 * It is where the raw source text, the six-field pins, the Git commit and the explicit
 * Unknown declarations stay readable after the business pages stopped showing them. The
 * redesign hid these fields from the rescue reader; it did not delete them, and it did not
 * weaken the rule that an unapproved editorial relation is reported as Unknown rather than
 * silently filled in.
 *
 * It was moved out of the old `ScenarioIssueWorkspace` unchanged, so the fidelity tests
 * that read it keep reading the same markup.
 */
export type DrawerContent =
  | { readonly kind: 'source' | 'unknowns' }
  | { readonly kind: 'entry'; readonly entry: ScenarioEntry }
  | { readonly kind: 'device'; readonly device: DeviceResearch };

export function SourceDrawer({
  data,
  content,
  issue,
  restoreFocusTo,
  onClose,
}: {
  readonly data: ScenarioProjection;
  readonly content: DrawerContent;
  readonly issue: string;
  readonly restoreFocusTo?: HTMLElement | null;
  readonly onClose: () => void;
}) {
  const dialog = useModalFocus(restoreFocusTo);
  const entryModule =
    content.kind === 'entry'
      ? (['risks', 'information', 'capability', 'machine', 'products'] as const).find((module) =>
          data.entries[module].includes(content.entry),
        )
      : undefined;
  const editorial =
    content.kind === 'entry' && entryModule
      ? entryStudyRelation(issue, entryModule, content.entry.code)
      : undefined;
  const explicitlyReferenced =
    content.kind === 'entry' &&
    (entryModule === 'information' || entryModule === 'capability') &&
    issueRelations[issue]?.[entryModule].includes(content.entry.code) === true;
  const riskContext =
    content.kind === 'entry' && entryModule === 'risks'
      ? issueRelations[content.entry.code]?.reason
      : undefined;
  return (
    <dialog
      className="scc-drawer"
      ref={dialog}
      aria-labelledby="scc-drawer-title"
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          const box = event.currentTarget.getBoundingClientRect();
          if (
            event.clientX < box.left ||
            event.clientX > box.right ||
            event.clientY < box.top ||
            event.clientY > box.bottom
          )
            onClose();
        }
      }}
    >
      <div className="scc-drawer-heading">
        <h2 id="scc-drawer-title">版本、来源与适用边界</h2>
        <button autoFocus onClick={onClose}>
          关闭 ×
        </button>
      </div>
      <div className="scc-drawer-body">
        {content.kind === 'entry' && (
          <>
            <span className="scc-chip">SOURCE_BOUND_SYNTHETIC_FIXTURE</span>
            <h3>
              {content.entry.code} · {content.entry.name}
            </h3>
            <p>源位置 {content.entry.sourcePath}</p>
            <div className="scc-callout">
              {mappingNature} · 当前问题：{issueLabels[issue] ?? issue}。研究关联示例 / 待 Owner
              审核。
              {entryModule === 'risks' ? (
                <p data-risk-context data-read-status={riskContext ? undefined : 'UNKNOWN'}>
                  {riskContext
                    ? '风险条目研究背景：' + riskContext
                    : '风险来源条目存在；编辑性研究背景尚未提供 / UNKNOWN。'}
                </p>
              ) : editorial ? (
                <>
                  <p data-entry-why>为什么需要：{editorial.why}</p>
                  <p data-entry-expectation>
                    {entryModule === 'information' ? '期待获取：' : '作用：'}
                    {editorial.expectation}
                  </p>
                </>
              ) : entryModule === 'machine' ? (
                <p>原源机器用途：{entryDescription(content.entry, 'machine')}</p>
              ) : explicitlyReferenced ? (
                <p data-read-status="MISSING_REFERENCE">
                  明确研究关联引用了该条目，但必要逐项说明缺失 / MISSING_REFERENCE。
                </p>
              ) : (
                <p data-read-status="UNKNOWN">
                  来源条目存在；当前问题未建立逐项编辑性关联 / UNKNOWN。
                </p>
              )}
            </div>
            <h3>完整原文与 Unknown</h3>
            <SourceFields value={content.entry.source} />
            {content.entry.related.length > 0 && (
              <>
                <h3>原源声明的关联</h3>
                <SourceFields value={content.entry.related} />
              </>
            )}
            {content.entry.unknowns.map((unknown) => (
              <div key={unknown.path}>
                <p>{content.entry.sourcePath + unknown.path}</p>
                <SourceFields value={unknown.value} />
              </div>
            ))}
          </>
        )}
        {content.kind === 'device' && (
          <>
            <span className="scc-chip scc-purple">EXTERNAL_PUBLIC_RESEARCH</span>
            <h3>{content.device.name}</h3>
            <p>{content.device.purpose}</p>
            <div className="scc-notice">{content.device.limits}</div>
            <p>本地资产归属、验证与可用状态：未提供 / UNKNOWN。</p>
            <p>公开来源页面核对 {publicSourceChecked}；不表示平台消防适用性验证。</p>
            {publicReferences
              .filter((item) => content.device.sources.includes(item.id))
              .map((item) => (
                <div className="scc-reference" key={item.id}>
                  <h3>{item.name}</h3>
                  <p>{item.type}</p>
                  <a href={item.url} target="_blank" rel="noopener noreferrer">
                    {item.url}
                  </a>
                </div>
              ))}
          </>
        )}
        {content.kind === 'unknowns' && <ScenarioCriticalUnknowns data={data} />}
        <div className="scc-notice">
          来源读取模式 {data.provenance.mode}。合成候选成员不代表发布、实战或运行。
        </div>
        <dl className="scc-source-status">
          {[
            ['ScenarioTemplate', data.provenance.pin.id + ' / v' + data.provenance.pin.version],
            [
              'MissionTemplate',
              data.provenance.missionTemplatePin.id +
                ' / v' +
                data.provenance.missionTemplatePin.version,
            ],
            [
              'ScenarioContentManifest',
              data.provenance.manifestPin.id + ' / v' + data.provenance.manifestPin.version,
            ],
            ['LocalScenarioProfile', 'HOLD-AS-EXPLICIT-UNKNOWN · 无 verified local mandate'],
            ['引用闭包', 'NOT VERIFIED'],
            ['Composition', 'NOT FORMED'],
            ['Package Identity', 'NONE'],
          ].map(([key, value]) => (
            <div key={key}>
              <dt>{key}</dt>
              <dd>{value}</dd>
            </div>
          ))}
        </dl>
        <details>
          <summary>完整六字段 pin 与 Git 技术信息</summary>
          <h3>ScenarioTemplate</h3>
          <SourceFields value={{ ...data.provenance.pin }} />
          <h3>MissionTemplate</h3>
          <SourceFields value={{ ...data.provenance.missionTemplatePin }} />
          <h3>ScenarioContentManifest</h3>
          <SourceFields value={{ ...data.provenance.manifestPin }} />
          <ScenarioSourceInspector data={data} />
        </details>
      </div>
    </dialog>
  );
}
